import logo from './logo.svg';
import './App.scss';
import { BrowserRouter, Route, Routes, useNavigate, useLocation } from 'react-router';
import Camera from './components/Camera';
import Header from './components/Header';
import Receipt from './components/Receipt';
import { initializeIcons } from '@fluentui/react';
import HomeScreen from './components/Home';
import Login from './components/Login';
import React, {useEffect, useRef, useState} from 'react';
import { AppContext } from './components/AppContext';
import FirebaseEmailAuth from './components/FirebaseEmailAuth'
import AdminPortal from './components/AdminPortal';
import ReceiptList from './components/Receipts';
import Receipts from './components/Receipts';
import ReceiptImage from './components/ReceiptImage';
import ProgressBar from './components/ProgressBar';
import Reports from './components/Reports';
import Merchant from './components/Merchant';
import ProductItem from './components/ProductItem';
import Menu from './components/Menu';
import ReceiptImages from './components/ReceiptImages';
import SConsciousApiService from './services/SConsciousApiService';
import { IAppData } from './interfaces/IAppData';
import Brand from './components/Brand';
import Category from './components/Category';
import Corporation from './components/Corporation';
import Stores from './components/Merchants';
import { onMessage } from 'firebase/messaging';
import { messaging } from './utils/firebase';
import HealthMap from './components/HeathMap';
import HeathMap from './components/HeathMap';
import History from './components/History';
import { usePWAUpdate } from './hooks/usePWAUpdate';
import ResetPassword from './components/ResetPassword';
import Chat from './components/Chat';
import Conversation from './components/Conversation';
import Features from './components/Features';
import PermissionsRequired from './components/PermissionsRequired';
import PWAInstallBanner from './components/PWAInstallBanner';
import Search from './components/Search';
import Profile from './components/Profile';
import Offline from './components/Offline';
import ManualReceipt from './components/ManualReceipt';
import BillSplitLive from './components/BillSplitLive';

const SWIPE_ROUTES = ['/', '/Search', '/Camera?p=Camera', '/Chat', '/Reports', '/Menu'];
const PULL_THRESHOLD = 72;

function SwipeNavigator({ children, onRefresh }: { children: React.ReactNode; onRefresh?: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPullingDown = useRef(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isPullingDown.current = false;
  };

  const PULL_EXCLUDED_ROUTES = ['/chat', '/conversation', '/search', '/', '/camera'];

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY.current === null || touchStartX.current === null || refreshing || !onRefresh) return;
    if (PULL_EXCLUDED_ROUTES.includes(location.pathname.toLowerCase())) return;
    const container = containerRef.current;
    if (!container || container.scrollTop > 0) return;

    const dy = e.touches[0].clientY - touchStartY.current;
    const dx = e.touches[0].clientX - touchStartX.current;

    if (dy > 10 && Math.abs(dy) > Math.abs(dx) * 1.5) {
      isPullingDown.current = true;
      setPullDistance(Math.min(dy * 0.5, PULL_THRESHOLD * 1.5));
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isPullingDown.current) {
      if (pullDistance >= PULL_THRESHOLD && onRefresh && !refreshing) {
        setRefreshing(true);
        onRefresh();
        setTimeout(() => setRefreshing(false), 1500);
      }
      setPullDistance(0);
      isPullingDown.current = false;
      touchStartX.current = null;
      touchStartY.current = null;
      return;
    }

    if (touchStartX.current === null || touchStartY.current === null) return;

    // Don't swipe-navigate when the user is interacting with a chart or slider
    const target = e.target as HTMLElement | SVGElement;
    if (target.closest?.('.recharts-wrapper') || target.closest?.('.date-range-slider')) {
      touchStartX.current = null;
      touchStartY.current = null;
      return;
    }

    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;

    // Only trigger for dominant horizontal swipes (>60px, more horizontal than vertical)
    if (Math.abs(dx) < 60 || Math.abs(dx) <= Math.abs(dy) * 1.5) return;

    const signedIn = localStorage.getItem('signed_in') === 'true';
    if (!signedIn) return;

    // Upload page: swipe left → home, swipe right → menu
    if (location.pathname + location.search === '/Camera?p=Upload') {
      navigate(dx < 0 ? '/' : '/Menu');
      return;
    }

    const currentIndex = SWIPE_ROUTES.indexOf(location.pathname + location.search);
    if (currentIndex === -1) return;

    if (dx < 0) {
      // Swipe left → next page (wraps to first)
      navigate(SWIPE_ROUTES[(currentIndex + 1) % SWIPE_ROUTES.length]);
    } else {
      // Swipe right → previous page (wraps to last)
      navigate(SWIPE_ROUTES[(currentIndex - 1 + SWIPE_ROUTES.length) % SWIPE_ROUTES.length]);
    }
  };

  const indicatorVisible = pullDistance > 0 || refreshing;
  const indicatorProgress = Math.min(pullDistance / PULL_THRESHOLD, 1);

  return (
    <div style={{ flex: 1, position: 'relative', overflowY: 'auto', overflowX: 'hidden', minHeight: 0, overscrollBehavior: 'none' }}>
      {/* Pull-to-refresh indicator */}
      {refreshing && (
        <div style={{
          position: 'absolute',
          inset: 0,
          zIndex: 9999,
          background: '#ffcc00',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <ProgressBar label="Loading" progress={5} />
        </div>
      )}
      <div
        ref={containerRef}
        style={{
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'none',
          transform: indicatorVisible && !refreshing ? `translateY(${pullDistance}px)` : undefined,
          transition: (!isPullingDown.current && !refreshing) ? 'transform 0.25s ease' : undefined,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>
    </div>
  );
}

function App() {

  initializeIcons(); // Call this once in your app, e.g. inside App.tsx

  //Init Services 
  let _sConsciousApiService = new SConsciousApiService();

  const storedEmail = localStorage.getItem('signed_in_email') || '';
  const storedUserId = localStorage.getItem('signed_in_id') || '';
  const storedFcmToken = localStorage.getItem('signed_in_fcm_token') || null;
  const storedCurrency = localStorage.getItem('currency') || 'EUR';
  const storedUserName = localStorage.getItem('user_name') || '';

  // PWA install banner: show when ?install=1 is in the URL
  const [showInstallBanner, setShowInstallBanner] = useState(
    new URLSearchParams(window.location.search).get('install') === '1' && !window.matchMedia('(display-mode: standalone)').matches
  );

  usePWAUpdate();

  const [appData, setAppData] = useState<IAppData>({
    user: {
      userId: storedUserId,
      userEmail: storedEmail,
      userName: storedUserName,
      signedIn: !!storedUserId,
      currency: storedCurrency,
      fcmToken: storedFcmToken,
    },
    newReport: false,
    newMessages: 0,
    scanning: [],
    analysisResult: null,
    loading: false,
    dataFetched: false,
    isOffline: !navigator.onLine,
    data: {version: '', brands: [], corporations: [], reportMerchants: [], reportReceipts: [], reportProductItems: [], logs: []}
  });

  const prevAppData = usePrevious(appData);

  function usePrevious<T>(value: T): T | undefined {
    const ref = useRef<T>(undefined);
    useEffect(() => {
      ref.current = value;
    }, [value]);
    return ref.current;
  }

  // Function to fetch queue data
  const fetchQueue = () => {
    if (storedUserId === "") return;
    _sConsciousApiService.listQueue(storedUserId).then((result: {fileName: string; image: string, status: string}[]) => {
      if (Array.isArray(result)) {

        // Detect any change: count difference OR status change on any item
        const prev = appData.scanning;
        const changed = prev.length !== result.length
          || prev.some((s, i) => !result[i] || result[i].status !== s.status || result[i].fileName !== s.fileName);

        if (changed) {
          //Indicate new report
          setAppData(prevAppData => ({ 
              ...prevAppData, 
              newReport: true,
              scanning: result.map(item => ({
                fileName: item.fileName,
                image: item.image,
                status: item.status
              })) 
            }) );

          // Get app data
          fetchAppData();
        }
        else {
          setAppData(prevAppData => ({
            ...prevAppData,
            scanning: result.map(item => ({
              fileName: item.fileName,
              image: item.image,
              status: item.status
            }))
          }));
        }
      }
    }).catch((error: any) => {
      console.error("Error fetching queue:", error);
    });
  };

  const fetchAppData = () => {
    _sConsciousApiService.getAppData(appData?.user.userId).then((result) => {
      //console.log("App data fetched successfully:", result);
      setAppData(prevAppData => ({
        ...prevAppData,
        dataFetched: true,
        data: {
          ...prevAppData.data,
          version: result.version || '',
          reportMerchants: result.reportMerchants || [],
          corporations: result.corporations || [],
          brands: result.brands || [],
          reportReceipts: result.reportReceipts || [],
          reportProductItems: result.reportProductItems || [],
          logs: result.logs || []
        }
      }));
    }).catch((error: any) => {
      console.error("Error fetching app data:", error);
    });
  };

  useEffect(() => {
    const unsub = onMessage(messaging, (payload) => {
      // Increment chat badge for incoming chat messages (skip if already on Chat/Conversation page)
      if (payload?.data?.type === 'chat' && payload?.data?.fromSelf !== 'true') {
        const path = window.location.pathname.toLowerCase();
        if (path === '/chat' || path === '/conversation') return;
        setAppData(prev => ({ ...prev, newMessages: (prev.newMessages || 0) + 1 }));
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const handleOnline = () => setAppData(prev => ({ ...prev, isOffline: false }));
    const handleOffline = () => setAppData(prev => ({ ...prev, isOffline: true }));
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {

    // Refresh user data from backend on startup to pick up any server-side changes
    if (storedUserId) {
      _sConsciousApiService.getUser(storedUserId, storedEmail || undefined).then((data: any) => {
        if (data && data.user_id) {
          const currency = data.currency || '';
          const userName = data.user_name || storedUserName;
          localStorage.setItem('currency', currency);
          localStorage.setItem('user_name', userName);
          setAppData(prev => ({
            ...prev,
            user: { ...prev.user, currency, userName, photoUrl: data.photo_url || undefined, discoverable: !!data.discoverable }
          }));
        }
      });

      // Compute unread chat count from conversations (server-side read_at is the source of truth).
      // Always sync — even setting to 0 — so any stale in-memory count is corrected.
      _sConsciousApiService.getChatConversations(storedUserId).then((convs: any[]) => {
        try {
          const unread = convs.filter((c: any) =>
            !c.pending && c.lastAt && (!c.readAt || new Date(c.lastAt) > new Date(c.readAt))
          ).length;
          setAppData(prev => ({ ...prev, newMessages: unread }));
        } catch { /* ignore parse errors */ }
      });
    }

    // Initial fetch
    fetchQueue();

    // Get app data
    fetchAppData();

  }, []);

  useEffect(() => {

    if (appData.loading === true) {

      //console.log("App is loading, fetching app data...");

      // Get app data
      fetchAppData();

      //Set loading false
      setAppData(prev => ({ ...prev, loading: false }));

    }

  }, [appData.loading]);

  useEffect(() => {
    // Set up interval to repeat every 5 seconds if scanning array is not empty
    let intervalId: NodeJS.Timeout | null = null;
    
    if (appData.scanning.length > 0) {
      intervalId = setInterval(() => {
        fetchQueue();
      }, 10000);
    }

    // Cleanup interval on component unmount or when scanning array changes
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [appData.scanning.length]);



  return (
    <AppContext.Provider value={{
      appData,
      setAppData: (newData: Partial<typeof appData>) => setAppData(prevData => ({ ...prevData, ...newData }))
    }}
    >
      <PWAInstallBanner show={showInstallBanner} onDismiss={() => setShowInstallBanner(false)} />
      <BrowserRouter>
        <div className="App" style={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden' }}>

          <Header />

          <SwipeNavigator onRefresh={() => { fetchAppData(); fetchQueue(); }}>
          <Routes>
            <Route path="/" element={<HomeScreen/>} />
            <Route path="/Receipts" element={<Receipts />} />
            <Route path="/Search" element={<Search />} />
            <Route path="/Profile" element={<Profile />} />
            <Route path="/Camera" element={<Camera />} />
            {/* <Route path="/Login" element={<FirebaseEmailAuth />} /> */}
            <Route path="/Receipt" element={<Receipt />} />
            <Route path="/Admin" element={<AdminPortal />} />
            <Route path="/ReceiptImage" element={<ReceiptImage />} />
            <Route path="/ProgressBar" element={<ProgressBar label={''} />} />
            <Route path="/Reports" element={<Reports />} />
            <Route path="/Merchant" element={<Merchant />} />
            <Route path="/ProductItem" element={<ProductItem />} />
            <Route path="/Menu" element={<Menu />} />
            <Route path="/ReceiptImages" element={<ReceiptImages />} />
            <Route path="/Brand" element={<Brand />} />
            <Route path="/Category" element={<Category />} />
            <Route path="/Corporation" element={<Corporation />} />
            <Route path="/Stores" element={<Stores />} />
            <Route path="/Heathmap" element={<HeathMap />} />
            <Route path="/Login" element={<Login />} />
            <Route path="/Chat" element={<Chat />} />
            <Route path="/Conversation" element={<Conversation />} />
            <Route path="/History" element={<History />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/Feedback" element={<Chat />} />
            <Route path="/Features" element={<Features />} />
            <Route path="/permissions-required" element={<PermissionsRequired />} />
            <Route path="/offline" element={<Offline />} />
            <Route path="/ManualReceipt" element={<ManualReceipt />} />
            <Route path="/BillSplit" element={<BillSplitLive />} />
          </Routes>
          </SwipeNavigator>

        </div>
      </BrowserRouter>
    </AppContext.Provider>
  );
}

export default App;
